import React, { useContext, useMemo } from "react";
import clsx from "clsx";
import { parseISO, format, isSameDay } from "date-fns";
import {
  Avatar,
  Badge,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  makeStyles,
} from "@material-ui/core";
import GroupIcon from "@material-ui/icons/Group";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    transition: "background-color 0.2s ease",
    cursor: "pointer",
    "&.Mui-selected": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(25, 118, 210, 0.2)"
          : "rgba(25, 118, 210, 0.15)",
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      "&:hover": {
        backgroundColor:
          theme.palette.type === "dark"
            ? "rgba(25, 118, 210, 0.25)"
            : "rgba(25, 118, 210, 0.2)",
      },
    },
  },
  ticketModernMobile: {
    borderRadius: 12,
    marginBottom: 4,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    paddingRight: 28,
  },
  lastMessageTime: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    marginLeft: 8,
  },
  contactLastMessage: {
    paddingRight: 36,
    maxWidth: "100%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  badgeStyle: {
    right: -3,
    top: 13,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: "0 4px",
  },
  secondaryContent: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 2,
    flexWrap: "wrap",
  },
  chip: {
    fontSize: "0.65rem",
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: 4,
    lineHeight: 1.4,
  },
  chipGroup: {
    backgroundColor: theme.palette.type === "dark" ? "#3949ab" : "#e8eaf6",
    color: theme.palette.type === "dark" ? "#fff" : "#3949ab",
  },
  chipDm: {
    backgroundColor: theme.palette.type === "dark" ? "#2e7d32" : "#e8f5e9",
    color: theme.palette.type === "dark" ? "#fff" : "#2e7d32",
  },
  menuBtn: {
    padding: 4,
  },
}));

const resolveAvatar = (chat, loggedInUser) => {
  const isGroup = chat?.isGroup === true || chat?.isGroup === "true";
  if (isGroup) {
    if (!chat.groupImage) return undefined;
    if (String(chat.groupImage).startsWith("http")) return chat.groupImage;
    return `${getBackendUrl()}${chat.groupImage}`;
  }
  const other = chat?.users?.find((u) => u.userId !== loggedInUser.id)?.user;
  if (!other?.profileImage) return undefined;
  if (String(other.profileImage).startsWith("http")) return other.profileImage;
  return `${getBackendUrl()}/public/company${other.companyId}/user/${other.profileImage}`;
};

const getChatTitle = (chat, loggedInUser) => {
  const isGroup = chat?.isGroup === true || chat?.isGroup === "true";
  if (isGroup) return chat.title || "Grupo";
  const other = chat?.users?.find((u) => u.userId !== loggedInUser.id)?.user;
  return other?.name || "Usuário";
};

const getUnread = (chat, loggedInUser) => {
  const current = chat?.users?.find((u) => u.userId === loggedInUser.id);
  return Number(current?.unreads) || 0;
};

const InternalChatListItem = ({ chat, selected, onSelect, onMenuClick }) => {
  const classes = useStyles();
  const { user: loggedInUser } = useContext(AuthContext);

  const isGroup = chat?.isGroup === true || chat?.isGroup === "true";
  const title = useMemo(() => getChatTitle(chat, loggedInUser), [chat, loggedInUser]);
  const unread = useMemo(() => getUnread(chat, loggedInUser), [chat, loggedInUser]);
  const avatarSrc = useMemo(() => resolveAvatar(chat, loggedInUser), [chat, loggedInUser]);

  const isOwner = Number(chat?.ownerId) === Number(loggedInUser?.id);
  const showMenu =
    typeof onMenuClick === "function" &&
    (loggedInUser.profile === "admin" || (isOwner && isGroup));

  const lastRaw =
    typeof chat?.lastMessage === "string"
      ? chat.lastMessage
      : chat?.lastMessage?.message;
  const lastMessagePreview =
    lastRaw || (isGroup ? "Grupo interno" : "Chat interno");

  const renderTime = () => {
    if (!chat?.updatedAt) return null;
    const date = parseISO(chat.updatedAt);
    return (
      <Typography className={classes.lastMessageTime} component="span">
        {isSameDay(date, new Date())
          ? format(date, "HH:mm")
          : format(date, "dd/MM/yyyy")}
      </Typography>
    );
  };

  return (
    <ListItem
      dense
      button
      selected={selected}
      onClick={() => onSelect(chat)}
      className={clsx(classes.ticket, classes.ticketModernMobile)}
    >
      <ListItemAvatar>
        <Badge
          badgeContent={unread}
          color="secondary"
          overlap="circular"
          classes={{ badge: classes.badgeStyle }}
          invisible={unread <= 0}
        >
          <Avatar src={avatarSrc}>
            {isGroup ? <GroupIcon fontSize="small" /> : title?.charAt(0) || "?"}
          </Avatar>
        </Badge>
      </ListItemAvatar>
      <ListItemText
        disableTypography
        primary={
          <span className={classes.contactNameWrapper}>
            <Typography noWrap component="span" variant="body2" style={{ fontWeight: 600 }}>
              {title}
            </Typography>
            {renderTime()}
          </span>
        }
        secondary={
          <>
            <Typography
              className={classes.contactLastMessage}
              noWrap
              component="span"
              variant="body2"
              color="textSecondary"
            >
              {String(lastMessagePreview)}
            </Typography>
            <div className={classes.secondaryContent}>
              <span
                className={clsx(
                  classes.chip,
                  isGroup ? classes.chipGroup : classes.chipDm
                )}
              >
                {isGroup ? "GRUPO" : "CHAT"}
              </span>
            </div>
          </>
        }
      />
      {showMenu && (
        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            className={classes.menuBtn}
            aria-label="mais opções"
            onClick={(e) => {
              e.stopPropagation();
              onMenuClick(e, chat);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};

export default InternalChatListItem;
export { getChatTitle, getUnread };
